import React, { useEffect, useState } from 'react';
import { ProgramDTO } from '@peartherapeutics/weaver-api-client';
import { sortingOrder } from 'utils/sort';
import pearClient from 'api/pearClient';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import Program from 'utils/program';
import useDebounce from 'custom-hooks/useDebounce';
import { DEFAULT_PAGINATION_LIMIT } from 'api/constants';
import usePaginationState from 'custom-hooks/usePaginationState';
import HeaderPaginated from '../ManagePatientAdherencePaginated/HeaderPaginated';
import PatientAdherenceTable from '../ManagePatientAdherencePaginated/PatientAdherenceTable';
import { PAGINATION_ACTIONS, updatePatientsSortedById } from './utils';

const ManagePatientAdherencePaginated = () => {
  // - retrieves global Program setting
  const [currentProgram, setProgram] = useState<ProgramDTO>(Program.get());

  // - Table and Table Status
  const initialParams: BaseParamsType = {
    locationId: '',
    queryString: '',
    sortOrder: sortingOrder.ASC,
    // given 'last_name' is the default on API, this enables 1st click to sort with 'desc'
    sortProp: 'last_name',
  };
  const [baseParams, setBaseParams] = useState(initialParams);
  const { locationId } = baseParams;

  const initialTableStatus: TableStatusType = {
    isLocationSet: false,
    isTableLoading: false,
    isTableShown: false,
  };

  const [tableStatus, setTableStatus] = useState(initialTableStatus);
  const { isLocationSet, isTableLoading, isTableShown } = tableStatus;

  // - PATIENT state...
  const [patients, setPatientState] = useState<
    IFormattedPatientAdherenceDetails[]
  >([]);

  const DEBOUNCE_TIMEOUT = 300;
  const debouncedQueryString = useDebounce(
    baseParams.queryString,
    DEBOUNCE_TIMEOUT,
  );

  const [paginationState, paginationActions] = usePaginationState({
    ...baseParams,
    currentProgram: currentProgram?.name,
    isLocationSet,
    queryString: debouncedQueryString!,
  });

  const { currentPage, patientIds, recordCount, hasFetchedIds } =
    paginationState;

  //* -----------------------------------------------

  const resetAllStates = () => {
    setPatientState([]);
    setTableStatus(initialTableStatus);
    setBaseParams(initialParams);
  };

  const handleLocationChange = async (locationId: string) => {
    setTableStatus({ ...tableStatus, isLocationSet: true });
    setBaseParams({ ...initialParams, locationId });
  };

  const handleLocationLoading = (isLoading: boolean) => {
    if (isLoading) {
      setTableStatus((prev) => ({ ...prev, isTableLoading: true }));
    } else {
      setTableStatus((prev) => ({ ...prev, isTableLoading: false }));
    }
  };

  const handleQueryStringChange = (queryString: string) => {
    setBaseParams({ ...baseParams, queryString });
  };

  const handleProgramChange = () => {
    resetAllStates();
  };

  const handleSortChange = (newProp: string) => {
    const { sortProp, sortOrder } = baseParams;
    if (newProp !== sortProp) {
      setBaseParams({
        ...baseParams,
        sortProp: newProp,
        sortOrder: sortingOrder.ASC,
      });
    } else {
      const newOrder =
        sortOrder === sortingOrder.DESC ? sortingOrder.ASC : sortingOrder.DESC;
      setBaseParams({ ...baseParams, sortOrder: newOrder });
    }
  };

  // Pagination Navigation ...
  const handlePageChange = async (action: string) => {
    switch (action) {
      case PAGINATION_ACTIONS.NEXT_PAGE:
        paginationActions.getNextPage();
        break;
      case PAGINATION_ACTIONS.PREVIOUS_PAGE:
        paginationActions.getPrevPage();
        break;
      case PAGINATION_ACTIONS.FIRST_PAGE:
      default:
        paginationActions.getFirstPage();
    }
  };

  //* -----------------------------------------------
  // MAIN effect- fetching Adherence Patients
  //* -----------------------------------------------

  useEffect(() => {
    let isValidToSetState = true;

    if (
      isLocationSet &&
      currentProgram &&
      hasFetchedIds &&
      patientIds.length !== 0
    ) {
      setTableStatus((prev) => ({
        ...prev,
        isTableShown: false,
        isTableLoading: true,
      }));
      pearClient.v2.clinicAdmin
        .getPatientAdherenceList({ patientIds, locationId })
        .then(({ data: adherenceData }) => {
          const patients = updatePatientsSortedById({
            patientIds,
            adherenceData,
          });

          if (isValidToSetState) {
            setPatientState(patients);
            setTableStatus((prev) => ({
              ...prev,
              isTableShown: true,
              isTableLoading: false,
            }));
          }
        })
        .catch((error) => {
          ErrorInterceptor.error(error);
          setTableStatus((prev) => ({
            ...prev,
            isTableShown: true,
            isTableLoading: false,
          }));
        });
    }

    return () => {
      isValidToSetState = false;
    };
  }, [
    locationId,
    hasFetchedIds,
    patientIds,
    debouncedQueryString,
    currentProgram,
    isLocationSet,
  ]);

  useEffect(() => {
    if (
      isLocationSet &&
      !hasFetchedIds &&
      patientIds.length === 0 &&
      patients.length !== 0
    ) {
      setPatientState([]);
      setTableStatus((prevState) => ({
        ...prevState,
        isTableShown: true,
        isTableLoading: false,
      }));
    }
  }, [hasFetchedIds, patientIds, isLocationSet, patients]);

  // Cure Material UI warning: 'TablePagination warning when page is out of range'
  // ... by ensuring current page is at least 0 when table is loading (appears empty)

  const rowsPerPage = parseInt(DEFAULT_PAGINATION_LIMIT, 10);
  const pageValue =
    currentPage > 0 && recordCount < rowsPerPage ? 0 : currentPage;

  return (
    <>
      <span data-testid="patient-adherence" />

      <HeaderPaginated
        patients={patients}
        onProgramChange={handleProgramChange}
        onClinicChange={handleLocationChange}
        onClinicLoading={handleLocationLoading}
        onQueryStringChange={handleQueryStringChange}
        searchTerm={baseParams.queryString}
        setProgram={setProgram}
        isTableLoading={isTableLoading}
      />

      <PatientAdherenceTable
        patients={patients}
        sortProp={baseParams.sortProp}
        sortOrder={baseParams.sortOrder}
        sortBy={handleSortChange}
        totalRecords={recordCount || 0}
        isTableShown={isTableShown}
        isTableLoading={isTableLoading}
        hasFetchedIds={hasFetchedIds}
        handlePageChange={handlePageChange}
        currentPage={pageValue}
      />
    </>
  );
};

export default ManagePatientAdherencePaginated;

export type BaseParamsType = {
  locationId: string;
  queryString?: string;
  sortOrder: SortOrderType;
  sortProp: string;
};

export type TableStatusType = {
  isLocationSet: boolean;
  isTableLoading: boolean;
  isTableShown: boolean;
};
